/**
 * Precision Mandate — the HPMS memo command center, native in Core.
 * Phase 1 surface, built to the approved visual: Command counters, agent
 * proposals strip, Asana-like memo list, and a memo view where the work is
 * always visible (operator hard rule: never row-collapse memo work).
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { orderActionsForViewer } from './orderActions';

const API = process.env.REACT_APP_API_URL || '';

const CLS_PILL = {
  Actionable: 'bg-orange-50 text-orange-700',
  Informational: 'bg-sky-50 text-sky-700',
};
const STATUS_PILL = {
  analyzing: 'bg-violet-50 text-violet-700',
  failed: 'bg-rose-50 text-rose-700',
  open: 'bg-blue-50 text-blue-700',
  'pending-review': 'bg-amber-50 text-amber-700',
  completed: 'bg-emerald-50 text-emerald-700',
  retired: 'bg-slate-100 text-slate-500',
};
const STATUS_LABEL = {
  analyzing: 'Analyzing', failed: 'Analysis failed', open: 'In progress',
  'pending-review': 'Pending review', completed: 'Completed', retired: 'Retired',
};

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return String(d).slice(0, 10); }
}

function fmtDateTime(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return String(d).slice(0, 16); }
}

function Pill({ map, value, label }) {
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${map[value] || 'bg-slate-100 text-slate-600'}`}>
      {label || value}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Command dashboard
// ---------------------------------------------------------------------------
function CommandBand({ counts }) {
  const items = [
    { n: counts?.open_memos ?? '—', l: 'Open memos', c: 'text-slate-900' },
    { n: counts?.due_week ?? '—', l: 'Due this week', c: 'text-orange-700' },
    { n: counts?.waiting_review ?? '—', l: 'Waiting on compliance', c: 'text-amber-700' },
    { n: counts?.unowned_actions ?? '—', l: 'Actions without an owner', c: (counts?.unowned_actions || 0) > 0 ? 'text-rose-700' : 'text-emerald-700' },
    { n: counts?.corrections ?? '—', l: 'Lessons the agent learned', c: 'text-violet-700' },
  ];
  return (
    <div className="flex gap-3.5">
      {items.map((it) => (
        <div key={it.l} className="flex-1 bg-white border border-slate-200 rounded-2xl px-4 py-3.5">
          <div className={`text-2xl font-bold tracking-tight ${it.c}`}>{it.n}</div>
          <div className="text-[11.5px] text-slate-500 font-medium mt-0.5">{it.l}</div>
        </div>
      ))}
    </div>
  );
}

function ActionQueue({ title, tone, rows, onOpen }) {
  if (!rows?.length) return null;
  const toneCls = tone === 'overdue' ? 'text-rose-700 bg-rose-50 border-rose-200' : 'text-amber-700 bg-amber-50 border-amber-200';
  return (
    <div className="flex-1 min-w-[300px] bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className={`px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider border-b ${toneCls}`}>
        {title} · {rows.length}
      </div>
      {rows.map((r) => (
        <button key={r.id} onClick={() => onOpen(r.memo_id)}
          className="w-full text-left px-4 py-2.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70">
          <div className="text-[12.5px] font-semibold text-slate-900 leading-snug">{r.action}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {r.memo_number} · {r.department_name} · <span className="font-semibold text-slate-700">{r.owner_name || 'NO OWNER'}</span> · due {fmtDate(r.external_due_date)}
          </div>
        </button>
      ))}
    </div>
  );
}

// Memos with no actions never reach the action queues, so they get their own
// band: who owns them and how much of the distribution list has read them.
function AckQueue({ rows, onOpen }) {
  if (!rows?.length) return null;
  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border-b border-blue-200">
        Memos to read and acknowledge · {rows.length}
      </div>
      {rows.map((r) => (
        <button key={r.id} onClick={() => onOpen(r.id)}
          className="w-full text-left px-4 py-2.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-semibold text-slate-900 truncate">{r.title}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {r.memo_number} · {r.owner_department_name || 'no department'} · <span className={`font-semibold ${r.owner_name ? 'text-slate-700' : 'text-rose-600'}`}>{r.owner_name || 'NO OWNER'}</span>
              {r.external_due_date ? ` · due ${fmtDate(r.external_due_date)}` : ''}
            </div>
          </div>
          <span className="text-[11px] font-semibold text-blue-700 whitespace-nowrap">read by {r.acknowledged} of {r.recipients}</span>
        </button>
      ))}
    </div>
  );
}

function DeptLoad({ rows }) {
  if (!rows?.length) return null;
  const max = Math.max(...rows.map((r) => r.open_count), 1);
  return (
    <div className="flex-1 min-w-[280px] bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
        Department workload · open actions
      </div>
      <div className="p-4 space-y-2.5">
        {rows.map((r) => (
          <div key={r.department_name}>
            <div className="flex justify-between text-[11.5px] mb-0.5">
              <span className="font-semibold text-slate-700">{r.department_name}</span>
              <span className="text-slate-500">{r.open_count}{r.overdue_count > 0 ? ` (${r.overdue_count} overdue)` : ''}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${Math.max(6, (r.open_count / max) * 100)}%`, background: r.overdue_count > 0 ? '#e11d48' : '#F58220' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ACTIVITY_LABEL = {
  memo_uploaded: 'uploaded a memo', memo_analyzed: 'agent finished analyzing',
  action_submitted: 'submitted an action', action_approved: 'approved an action',
  action_returned: 'returned an action', action_reassigned: 'reassigned an action',
  evidence_added: 'attached evidence', comment_added: 'commented',
  mention_email: 'mentioned teammates, one email sent',
  proposal_approved: 'approved an agent proposal', proposal_dismissed: 'dismissed an agent proposal',
};

function ActivityFeed({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="flex-1 min-w-[280px] bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
        Recent activity
      </div>
      {rows.map((r, i) => (
        <div key={i} className="px-4 py-2 border-b border-slate-100 last:border-b-0 text-[11.5px] text-slate-600">
          <span className="font-semibold text-slate-800">{r.actor || 'The agent'}</span> {ACTIVITY_LABEL[r.event_type] || r.event_type}
          {r.memo_number ? <span className="text-slate-400"> · {r.memo_number}</span> : null}
          <span className="text-slate-400"> · {fmtDate(r.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent proposals strip — the agent proposes, compliance decides.
// ---------------------------------------------------------------------------
const PROPOSAL_TITLES = {
  policy_impact: 'Policy impact found',
  calendar_dates: 'Calendar dates detected',
  risk_entry: 'Possible risk register entry',
};

function ProposalDetail({ p }) {
  const pay = p.payload || {};
  if (p.kind === 'calendar_dates') {
    return (
      <div className="mt-1.5 space-y-1">
        {(pay.entries || []).map((e, i) => (
          <div key={i} className="text-[11.5px] text-slate-600 bg-violet-50/70 rounded-md px-2 py-1">
            <span className="font-semibold">{fmtDate(e.date)}</span> — {e.what}
            {e.quote ? <span className="text-slate-400 italic"> · "{e.quote.slice(0, 90)}"</span> : null}
          </div>
        ))}
      </div>
    );
  }
  if (p.kind === 'policy_impact') {
    return (
      <div className="mt-1.5 space-y-1">
        {(pay.policies || []).map((pl, i) => (
          <div key={i} className="text-[11.5px] text-slate-600 bg-violet-50/70 rounded-md px-2 py-1">
            <span className="font-semibold">{pl.policy_number} {pl.title}</span> — {pl.reason}
            {pl.quote ? <span className="text-slate-400 italic"> · "{pl.quote.slice(0, 90)}"</span> : null}
          </div>
        ))}
      </div>
    );
  }
  if (p.kind === 'risk_entry') {
    return (
      <div className="mt-1.5 text-[11.5px] text-slate-600 bg-violet-50/70 rounded-md px-2 py-1">
        <span className="font-semibold">{pay.title}</span>
        <div className="mt-0.5 whitespace-pre-wrap">{(pay.description || '').slice(0, 240)}</div>
      </div>
    );
  }
  return null;
}

function ProposalCard({ p, onDecide }) {
  return (
    <div className="flex-1 min-w-[280px] bg-white border border-violet-200 rounded-xl px-3.5 py-3">
      <div className="text-[12.5px] font-semibold text-slate-900">{PROPOSAL_TITLES[p.kind] || p.kind}</div>
      <div className="text-xs text-slate-600 mt-1 leading-relaxed">
        {p.payload?.summary || `${p.memo_number || ''} ${p.memo_title || ''}`}
      </div>
      <ProposalDetail p={p} />
      <div className="mt-2 flex items-center gap-2.5">
        <button onClick={() => onDecide(p.id, 'approve')}
          className="px-3 py-1 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-[11.5px] font-semibold">
          Approve and create
        </button>
        <button onClick={() => onDecide(p.id, 'dismiss')}
          className="text-[11.5px] text-slate-500 hover:text-slate-700">Dismiss</button>
      </div>
    </div>
  );
}

function ProposalsStrip({ proposals, onDecide }) {
  if (!proposals?.length) return null;
  return (
    <div className="mt-4 bg-violet-50/60 border border-violet-200 rounded-2xl p-4">
      <div className="text-[11px] font-bold uppercase tracking-wider text-violet-700 mb-2.5">
        Agent proposals · the agent proposes, compliance decides
      </div>
      <div className="flex gap-3 flex-wrap">
        {proposals.map((p) => <ProposalCard key={p.id} p={p} onDecide={onDecide} />)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------
function UploadButton({ onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pick = () => inputRef.current?.click();
  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (!files.length) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const r = await fetch(`${API}/api/mnd/memos`, { method: 'POST', credentials: 'include', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Upload failed');
      onDone?.(d.memo_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      {error ? <span className="text-xs text-rose-600">{error}</span> : null}
      <input ref={inputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.csv" className="hidden" onChange={onFiles} />
      <button onClick={pick} disabled={busy}
        className="px-5 py-2.5 rounded-xl text-white text-[13.5px] font-semibold shadow-md disabled:opacity-60"
        style={{ background: 'linear-gradient(135deg,#F58220,#f59e0b)', boxShadow: '0 2px 8px rgba(245,130,32,.3)' }}>
        {busy ? 'Uploading…' : '⇡ Upload memo'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Memo list
// ---------------------------------------------------------------------------
// ── Filter bar ───────────────────────────────────────────────────────────────
// Server-side filtering for the memo lists. Every control writes one field of
// a filters object; the page debounces the object into query params on
// GET /api/mnd/memos. All filtering happens in SQL — the old standalone app
// filtered in the browser over a full fetch, and that pattern stops here.
// This bar belongs to LIST screens only; MemoView never grows one.
const EMPTY_FILTERS = { q: '', status: '', department: '', owner: '', classification: '', urgency: '', received_from: '', received_to: '', due_from: '', due_to: '', sort: '' };
const filtersActive = (f) => Object.entries(f).some(([k, v]) => k !== 'sort' && String(v || '').trim());
const SORT_LABELS = { '': 'Due date', due: 'Due date', received: 'Received (newest)', received_asc: 'Received (oldest)', updated: 'Last updated', created: 'Created', title: 'Title' };

function FilterBar({ filters, onChange, statusOptions, deptOptions, people, isCompliance, shown, total }) {
  const set = (k) => (e) => onChange({ ...filters, [k]: e.target.value });
  const active = Object.entries(filters).some(([k, v]) => k !== 'sort' && v);
  const sel = 'px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs text-slate-700';
  const dat = 'px-2 py-1 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 w-[8.2rem]';
  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-2xl px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <input value={filters.q} onChange={set('q')}
          placeholder="Search by memo number, title, action, or department"
          className="flex-1 min-w-[220px] max-w-md px-3 py-1.5 rounded-lg border border-slate-200 text-xs" />
        <select value={filters.status} onChange={set('status')} className={sel}>
          <option value="">Status: All</option>
          {statusOptions.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
        </select>
        <select value={filters.department} onChange={set('department')} className={sel}>
          <option value="">Department: All</option>
          {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filters.classification} onChange={set('classification')} className={sel}>
          <option value="">Type: All</option>
          <option value="Actionable">Actionable</option>
          <option value="Informational">Informational</option>
        </select>
        <select value={filters.urgency} onChange={set('urgency')} className={sel}>
          <option value="">Urgency: All</option>
          <option value="Urgent">Urgent</option>
          <option value="High">High</option>
          <option value="Routine">Routine</option>
        </select>
        {isCompliance && (
          <select value={filters.owner} onChange={set('owner')} className={sel}>
            <option value="">Owner: All</option>
            {(people?.users || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Received</span>
        <input type="date" value={filters.received_from} onChange={set('received_from')} className={dat} />
        <span className="text-xs text-slate-400">to</span>
        <input type="date" value={filters.received_to} onChange={set('received_to')} className={dat} />
        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide ml-2">Due</span>
        <input type="date" value={filters.due_from} onChange={set('due_from')} className={dat} />
        <span className="text-xs text-slate-400">to</span>
        <input type="date" value={filters.due_to} onChange={set('due_to')} className={dat} />
        <select value={filters.sort} onChange={set('sort')} className={`${sel} ml-2`}>
          {Object.entries(SORT_LABELS).filter(([k]) => k !== 'due').map(([k, l]) => (
            <option key={k || 'default'} value={k}>Sort: {l}</option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-2.5">
          {typeof total === 'number' && (
            <span className="text-xs text-slate-400">{active ? `${shown} of ${total}` : `${total} memo${total === 1 ? '' : 's'}`}</span>
          )}
          {active && (
            <button onClick={() => onChange({ ...EMPTY_FILTERS, sort: filters.sort })}
              className="text-xs font-semibold text-orange-600 hover:text-orange-700">Clear filters</button>
          )}
        </div>
      </div>
    </div>
  );
}

const MEMO_GRID = 'grid grid-cols-[minmax(260px,2.6fr)_minmax(104px,0.9fr)_minmax(150px,1.5fr)_minmax(96px,0.7fr)_minmax(112px,0.9fr)_minmax(160px,1.2fr)] items-center gap-3.5 px-4';

// What someone else did on the memo since this person last saw it.
const NEW_ACTIVITY_VERB = {
  submitted: 'submitted an action', commented: 'commented',
  replied: 'replied by email', acknowledged: 'acknowledged the memo',
};

function MemoList({ memos, onOpen, title = 'Memos', emptyText, search, onSearch, sortLabel, onCleared }) {
  // Clearing is per person and instant on screen; the server remembers it.
  const [cleared, setCleared] = useState(() => new Set());
  const clearedKey = (m) => `${m.id}|${m.new_activity?.at || ''}`;
  const clearHighlight = async (e, m) => {
    const id = m.id;
    e.stopPropagation();
    setCleared((prev) => new Set(prev).add(clearedKey(m)));
    try {
      await fetch(`${API}/api/mnd/memos/${id}/seen`, { method: 'POST', credentials: 'include' });
      if (onCleared) onCleared();
    } catch { /* the highlight returns on the next load if the save failed */ }
  };
  const newCount = memos.filter((m) => m.new_activity && !cleared.has(clearedKey(m))).length;
  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 px-4 py-2.5 gap-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
          {title} · {memos.length}
          {newCount > 0 && <span className="ml-2 px-2 py-0.5 rounded-full bg-orange-500 text-white normal-case tracking-normal">{newCount} with new activity</span>}
        </div>
        {onSearch ? (
          <input value={search} onChange={(e) => onSearch(e.target.value)}
            placeholder="Search by memo number, title, action, or department"
            className="flex-1 max-w-md px-3 py-1.5 rounded-lg border border-slate-200 text-xs" />
        ) : <div className="text-xs text-slate-400">Sorted by {sortLabel || 'due date'}</div>}
      </div>
      {memos.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-slate-400">
          {emptyText || 'No memos yet. Upload the first HPMS memo and the agent will brief it, extract the actions, and route them.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[980px]">
            <div className={`${MEMO_GRID} py-2 border-b border-slate-200 bg-slate-50/60`}>
              {['Memo', 'Classification', 'Departments', 'Due', 'Status', 'Owner'].map((h) => (
                <div key={h} className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">{h}</div>
              ))}
            </div>
            {memos.map((m) => {
              const fresh = m.new_activity && !cleared.has(clearedKey(m)) ? m.new_activity : null;
              return (
              <div key={m.id} role="button" tabIndex={0} onClick={() => onOpen(m.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(m.id); } }}
                className={`${MEMO_GRID} w-full text-left py-3 border-b border-slate-100 cursor-pointer ${fresh
                  ? 'bg-orange-50 hover:bg-orange-100/70 shadow-[inset_4px_0_0_0_#f97316]'
                  : 'hover:bg-slate-50/70'}`}>
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold text-slate-900 leading-snug">{m.title}</div>
                  <div className="text-[11px] text-slate-400 font-mono mt-0.5">{m.memo_number}</div>
                  {fresh && (
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <span className="px-1.5 py-0.5 rounded bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider">New</span>
                      <span className="text-[11.5px] font-semibold text-orange-800">
                        {fresh.by || 'Someone'} {NEW_ACTIVITY_VERB[fresh.kind] || 'commented'} · {fmtDateTime(fresh.at)}
                      </span>
                      <button type="button" onClick={(e) => clearHighlight(e, m)}
                        title="Remove the highlight without opening the memo"
                        className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 underline underline-offset-2">
                        Clear
                      </button>
                    </div>
                  )}
                </div>
                <div className="min-w-0"><Pill map={CLS_PILL} value={m.classification} /></div>
                <div className="min-w-0 flex flex-wrap gap-1">
                  {(() => {
                    const fromActions = (Array.isArray(m.departments) ? m.departments : []).filter(Boolean);
                    const list = fromActions.length ? fromActions : (m.owner_department_name ? [m.owner_department_name] : []);
                    return list.slice(0, 3).map((d) => (
                      <span key={d} className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-semibold">{d}</span>
                    ));
                  })()}
                </div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">{fmtDate(openDueDate(m))}</div>
                <div className="min-w-0"><Pill map={STATUS_PILL} value={m.status} label={STATUS_LABEL[m.status]} /></div>
                <div className="min-w-0">
                  <div className={`text-xs font-semibold truncate ${m.owner_name ? 'text-slate-700' : 'text-rose-600'}`}>
                    {m.owner_name || 'No owner'}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {m.action_count > 0
                      ? `${m.approved_count} of ${m.action_count} approved`
                      : (m.recipient_count > 0 ? `read by ${m.acknowledged_count} of ${m.recipient_count}` : 'nothing to do, needs acknowledgement')}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Teach the agent — org-level, compliance-only. The agent reads back what it
// learned (rendered from the stored rule, never model prose); compliance
// rebuts until it is right; only a confirmed read-back is enforced.
// ---------------------------------------------------------------------------
function TeachingReadback({ t, busy, onConfirm, onRebutStart, onDiscard, rebutOpen, rebutText, setRebutText, onRebutSend }) {
  const understood = t.parsed?.understood;
  return (
    <div className="bg-white rounded-xl border border-violet-200 p-4">
      <div className="text-[10.5px] font-bold uppercase tracking-wider text-violet-700 mb-1.5">What I learned</div>
      <div className="text-xs text-slate-800 leading-relaxed whitespace-pre-wrap">{t.readback}</div>
      <div className="text-[11px] text-slate-400 mt-2">You taught: “{t.guidance}”</div>
      {!rebutOpen ? (
        <div className="mt-3 flex items-center gap-2">
          <button disabled={busy || !understood} onClick={onConfirm}
            className="px-3.5 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
            That’s right — apply it
          </button>
          <button disabled={busy} onClick={onRebutStart}
            className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:border-slate-400 text-slate-700 text-xs font-semibold">
            Not quite
          </button>
          <button disabled={busy} onClick={onDiscard} className="text-[11px] font-semibold text-slate-400 hover:text-rose-600 ml-1">Discard</button>
          {!understood && <span className="text-[11px] text-amber-700 font-semibold">Answer the agent’s question above, then re-explain.</span>}
        </div>
      ) : (
        <div className="mt-3">
          <textarea value={rebutText} onChange={(e) => setRebutText(e.target.value)} rows={2}
            placeholder="Tell the agent what it got wrong, in plain words"
            className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400" />
          <div className="mt-1.5 flex gap-2">
            <button disabled={busy || !rebutText.trim()} onClick={onRebutSend}
              className="px-3.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold disabled:opacity-50">
              Re-explain
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// The modal lives at the MEMO (operator order 2026-08-21): the user talks
// to the agent while looking at the work — about one specific action (the
// rule anchors to that action's own text) or about everything, org-wide.
function TalkToAgentModal({ open, onClose, actions = [], presetActionId = null }) {
  const [scope, setScope] = useState(presetActionId || 'global');
  const [teachings, setTeachings] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [guidance, setGuidance] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [rebutFor, setRebutFor] = useState(null);
  const [rebutText, setRebutText] = useState('');
  const [answerFor, setAnswerFor] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [showTaught, setShowTaught] = useState(false);
  useEffect(() => { setScope(presetActionId || 'global'); setShowTaught(false); }, [presetActionId, open]);

  const load = useCallback(() => {
    fetch(`${API}/api/mnd/agent/teachings`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => { if (d?.success) setTeachings(d.teachings); }).catch(() => {});
    fetch(`${API}/api/mnd/agent/questions`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => { if (d?.success) setQuestions(d.questions); }).catch(() => {});
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const call = async (path, body, method = 'POST') => {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/mnd/agent/${path}`, {
        method, credentials: 'include',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Request failed');
      return d;
    } catch (e) { setError(e.message); return null; }
    finally { setBusy(false); }
  };

  const drafts = teachings.filter((t) => t.status === 'awaiting_confirmation');
  const active = teachings.filter((t) => t.status === 'active');

  if (!open) return null;
  const scopedAction = scope !== 'global' ? actions.find((a) => a.id === scope) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-slate-900/40" onClick={onClose} style={{ backdropFilter: 'blur(4px)' }} />
      <div className="relative bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 w-full max-w-3xl max-h-[88vh] flex flex-col min-h-0">
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[16px] font-semibold text-slate-900">Talk to the agent</h2>
            <p className="text-[11.5px] text-slate-400 mt-0.5">
              Tell it when an action is written wrongly, should not exist, or is missing a step that must come first. It answers with what it learned; nothing applies until you confirm.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none flex-shrink-0" aria-label="Close">×</button>
        </div>
        <div className="px-6 py-4 overflow-y-auto min-h-0">
      {error && <div className="mb-2 text-[11px] font-semibold text-rose-600">{error}</div>}
      <div className="flex items-center gap-2 text-[11.5px] text-slate-600">
        <span className="text-slate-400 font-semibold">About</span>
        <select value={scope} onChange={(e) => setScope(e.target.value)}
          className="flex-1 min-w-0 text-[11.5px] text-slate-700 border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:border-orange-400 truncate">
          <option value="global">This organization in general</option>
          {actions.map((a, i) => (
            <option key={a.id} value={a.id}>{i + 1}. {a.action}</option>
          ))}
        </select>
      </div>

      {questions.length > 0 && (
        <div className="mt-3 space-y-2.5">
          {questions.map((q) => (
            <div key={q.id} className="bg-white rounded-xl border border-amber-200 p-4">
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-amber-700 mb-1">The agent asks{q.memo_number ? ` · memo ${q.memo_number}` : ''}</div>
              <div className="text-xs text-slate-800 leading-relaxed">{q.question}</div>
              {answerFor === q.id ? (
                <div className="mt-2.5">
                  <textarea value={answerText} onChange={(e) => setAnswerText(e.target.value)} rows={2}
                    placeholder="Answer in plain words — your answer becomes a teaching the agent reads back for your confirmation"
                    className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400" />
                  <div className="mt-1.5 flex gap-2">
                    <button disabled={busy || !answerText.trim()} onClick={async () => {
                      const d = await call('teachings', { guidance: answerText.trim(), question_id: q.id });
                      if (d) { setAnswerFor(null); setAnswerText(''); load(); }
                    }} className="px-3.5 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">Answer & teach</button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex gap-3">
                  <button disabled={busy} onClick={() => { setAnswerFor(q.id); setAnswerText(''); }}
                    className="text-[11.5px] font-semibold text-orange-700 hover:text-orange-900">Answer</button>
                  <button disabled={busy} onClick={async () => { await call(`questions/${q.id}/dismiss`); load(); }}
                    className="text-[11.5px] font-semibold text-slate-400 hover:text-slate-600">Dismiss</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 bg-white rounded-xl border border-slate-200 p-3.5">
        <textarea value={guidance} onChange={(e) => setGuidance(e.target.value)} rows={3}
          placeholder={scopedAction
            ? 'e.g. “this action should have asked for the submission confirmation number” · “this should never have been created — we are not a snowflake plan” · “before this, an action to pull the current file from HPMS is needed”'
            : 'Give the agent a standing instruction about how actions should be created for this organization'}
          className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400" />
        <div className="mt-1.5 flex items-center justify-between gap-2.5 flex-wrap">
          <span className="text-[10.5px] text-slate-400">Wrong person or department? Use <span className="font-semibold text-slate-500">Reassign</span> on the action — the agent learns from every reassignment.</span>
          <button disabled={busy || !guidance.trim()} onClick={async () => {
            const d = await call('teachings', { guidance: guidance.trim(), ...(scope !== 'global' ? { action_id: scope } : {}) });
            if (d) { setGuidance(''); load(); }
          }} className="px-3.5 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
            {busy ? 'The agent is reading…' : 'Teach'}
          </button>
        </div>
      </div>

      {drafts.length > 0 && (
        <div className="mt-3 space-y-2.5">
          {drafts.map((t) => (
            <TeachingReadback key={t.id} t={t} busy={busy}
              rebutOpen={rebutFor === t.id} rebutText={rebutText} setRebutText={setRebutText}
              onConfirm={async () => { const d = await call(`teachings/${t.id}/confirm`); if (d) load(); }}
              onRebutStart={() => { setRebutFor(t.id); setRebutText(''); }}
              onRebutSend={async () => {
                const d = await call(`teachings/${t.id}/rebut`, { guidance: rebutText.trim() });
                if (d) { setRebutFor(null); setRebutText(''); load(); }
              }}
              onDiscard={async () => { const d = await call(`teachings/${t.id}`, null, 'DELETE'); if (d) load(); }} />
          ))}
        </div>
      )}

      {active.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <button onClick={() => setShowTaught((v) => !v)} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">
            What I’ve been taught · {active.length} {showTaught ? '▴' : '▾'}
          </button>
          {showTaught && (
            <div className="space-y-1.5 mt-2">
              {active.map((t) => (
                <div key={t.id} className="bg-slate-50 rounded-lg border border-slate-200 px-3.5 py-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{t.readback}</div>
                    <div className="text-[10.5px] text-slate-400 mt-1">Confirmed by {t.confirmed_by_name || 'compliance'} · {fmtDate(t.confirmed_at)}</div>
                  </div>
                  <button disabled={busy} title="Remove — stops applying to future memos, never rewrites the past"
                    onClick={async () => { const d = await call(`teachings/${t.id}`, null, 'DELETE'); if (d) load(); }}
                    className="text-slate-300 hover:text-rose-600 text-base leading-none flex-shrink-0">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Memo view — work always visible. No collapsed rows, ever.
// ---------------------------------------------------------------------------
const ACTION_STATUS = {
  pending: { label: 'Pending', cls: 'text-slate-500' },
  'in-progress': { label: 'In progress', cls: 'text-blue-700' },
  submitted: { label: 'Submitted · with compliance', cls: 'text-amber-700' },
  returned: { label: 'Returned · needs rework', cls: 'text-rose-700' },
  approved: { label: 'Approved', cls: 'text-emerald-700' },
  'not-applicable': { label: 'Not applicable', cls: 'text-slate-400' },
};

// ── Comment thread, Asana style ──────────────────────────────────────────
// Every comment is a person: avatar, name, when, what they said. The color
// is derived from the name so the same person is always the same color.
const AVATAR_COLORS = ['bg-orange-500', 'bg-sky-500', 'bg-emerald-500', 'bg-violet-500', 'bg-rose-500', 'bg-amber-500', 'bg-teal-600', 'bg-indigo-500'];
function avatarColor(name) {
  let h = 0;
  for (const ch of String(name || '')) h = ((h * 31) + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}
function fmtWhen(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const sameYear = dt.getFullYear() === new Date().getFullYear();
  return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }), hour: 'numeric', minute: '2-digit' });
}

function Avatar({ name, size = 'w-7 h-7 text-[10.5px]' }) {
  return (
    <div className={`${size} rounded-full ${avatarColor(name)} text-white font-bold flex items-center justify-center flex-shrink-0`}>
      {initialsOf(name)}
    </div>
  );
}

function CommentThread({ comments }) {
  if (!comments?.length) return null;
  return (
    <div className="space-y-3">
      {comments.map((c) => (
        <div key={c.id} className="flex gap-2.5">
          <Avatar name={c.author} />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-semibold text-slate-800">{c.author}</span>
              <span className="text-[10.5px] text-slate-400">{fmtWhen(c.created_at)}</span>
            </div>
            <div className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap mt-0.5">{c.body}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// One fetch per session for the @ picker; every comment box shares it.
let MENTION_CACHE = null;
function useMentionablePeople() {
  const [people, setPeople] = useState(MENTION_CACHE || []);
  useEffect(() => {
    if (MENTION_CACHE) return;
    let alive = true;
    fetch(`${API}/api/mnd/people/mentionable`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d?.people) return;
        MENTION_CACHE = d.people;
        setPeople(d.people);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return people;
}

function ActionCommentBox({ actionId = null, memoId, meName = null, onPosted }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [highlight, setHighlight] = useState(0);
  const taRef = useRef(null);
  const people = useMentionablePeople();

  // The token being typed after an @, if the caret is inside one. Names carry
  // spaces, so the token is allowed to hold one space and still match.
  const readMentionToken = (value, caret) => {
    const upto = value.slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at === -1) return null;
    if (at > 0 && /[\w@.]/.test(upto[at - 1])) return null;
    const token = upto.slice(at + 1);
    if (!/^[A-Za-z0-9._'-]*(?: [A-Za-z0-9._'-]*)?$/.test(token)) return null;
    return { at, token };
  };

  const suggestions = (() => {
    if (!mentionQuery) return [];
    const q = mentionQuery.token.trim().toLowerCase();
    const pool = people.filter((p) => {
      if (!q) return true;
      return String(p.name || '').toLowerCase().includes(q)
        || String(p.email || '').toLowerCase().startsWith(q);
    });
    return pool.slice(0, 6);
  })();

  const onChange = (e) => {
    const value = e.target.value;
    setBody(value);
    const tok = readMentionToken(value, e.target.selectionStart ?? value.length);
    setMentionQuery(tok);
    setHighlight(0);
  };

  const insertMention = (person) => {
    if (!mentionQuery) return;
    const caret = taRef.current?.selectionStart ?? body.length;
    const before = body.slice(0, mentionQuery.at);
    const after = body.slice(caret);
    const next = `${before}@${person.name} ${after}`;
    setBody(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      const pos = (before + `@${person.name} `).length;
      taRef.current?.focus();
      taRef.current?.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % suggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(suggestions[highlight]); }
    else if (e.key === 'Escape') { setMentionQuery(null); }
  };
  const post = async () => {
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/mnd/memos/${memoId}/comments`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: text, action_id: actionId || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || 'Could not post the comment');
      setBody('');
      setOpen(false);
      onPosted?.();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };
  // Asana's composer pattern: a quiet one-line invitation that expands into a
  // real writing box the moment it is touched, with your own avatar beside it
  // so the thread reads as people talking, not fields being filled.
  return (
    <div className="flex gap-2.5 items-start">
      <Avatar name={meName || 'Me'} />
      <div className="relative flex-1 min-w-0">
        {!open && !body ? (
          <button type="button"
            onClick={() => { setOpen(true); requestAnimationFrame(() => taRef.current?.focus()); }}
            className="w-full text-left px-3.5 py-2 rounded-full border border-slate-200 bg-white text-xs text-slate-400 hover:border-slate-300 hover:text-slate-500">
            Ask a question or post an update
          </button>
        ) : (
          <div className="rounded-xl border border-slate-300 bg-white focus-within:border-slate-400 overflow-hidden shadow-sm">
            <textarea ref={taRef} value={body} onChange={onChange} onKeyDown={onKeyDown} rows={3} autoFocus
              placeholder="Write your update. Type @ to bring a teammate into the thread."
              className="w-full px-3.5 py-2.5 text-xs leading-relaxed resize-none outline-none border-0 block" />
            <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-slate-50 border-t border-slate-100">
              <span className="text-[10.5px] text-slate-400">An @ mention emails that person immediately</span>
              <div className="flex gap-1.5">
                <button type="button" onClick={() => { setBody(''); setOpen(false); setMentionQuery(null); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-100">
                  Cancel
                </button>
                <button disabled={busy || !body.trim()} onClick={post}
                  className="px-3.5 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold disabled:opacity-40">
                  {busy ? 'Posting…' : 'Post'}
                </button>
              </div>
            </div>
          </div>
        )}
        {suggestions.length > 0 && (
          <div className="absolute z-20 left-2 bottom-full mb-1 w-72 bg-white border border-slate-300 rounded-xl shadow-lg overflow-hidden">
            <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 border-b border-slate-200">
              Tag a teammate
            </div>
            {suggestions.map((p, i) => (
              <button key={p.id} type="button"
                onMouseDown={(e) => { e.preventDefault(); insertMention(p); }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-3 py-2 text-xs ${i === highlight ? 'bg-orange-50' : 'bg-white'} hover:bg-orange-50`}>
                <div className="font-semibold text-slate-800">{p.name}</div>
                <div className="text-[11px] text-slate-400">{p.email}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Correspondence (operator order 2026-10-30): every email sent about the
 * memo through Workstream and every reply that came back, as emails. On an
 * actionable memo each action shows its own; an informational memo shows
 * them on the memo. "Open email" hands over the .eml for the reader's mail
 * program; "Reply all" opens a reply to everyone on the email plus the PCG
 * reply address, so the answer files itself here as well.
 */
function CorrespondencePanel({ memoId, rows, compact = false, title = 'Correspondence' }) {
  const [open, setOpen] = useState(!compact);
  const list = rows || [];
  // Reply all and Follow up (operator order 2026-11-02): an unsent email for
  // the reader's own mail program, everyone on the email plus the PCG reply
  // address on To, the prior email whole beneath, formatting kept, never
  // cut. Sent from the mailbox, it files on the memo through the PCG address.
  const draft = (c, mode) => `${API}/api/mnd/memos/${memoId}/correspondence/${c.id}/reply.eml?mode=${mode}`;
  if (compact && !list.length) return null;
  const head = (
    <button type="button" onClick={() => setOpen((v) => !v)}
      className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${compact ? 'text-slate-500 hover:text-slate-800' : 'text-slate-500'}`}
      title="Every email sent and received about this, as emails">
      <span aria-hidden>✉️</span> {title} · {list.length}{compact ? <span className="text-slate-300 font-normal normal-case tracking-normal ml-1">{open ? '▴' : '▾'}</span> : null}
    </button>
  );
  return (
    <div className={compact ? 'mt-2.5' : 'mt-5 pt-4 border-t border-slate-100'}>
      {head}
      {open && (
        <div className="mt-2 space-y-1.5">
          {!list.length && <div className="text-[11.5px] text-slate-400">No emails yet. A Workstream follow-up about this memo, and every reply to it, will appear here.</div>}
          {list.map((c) => {
            const sent = c.direction === 'sent';
            return (
              <div key={c.id} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${sent ? 'bg-sky-50 text-sky-700' : 'bg-emerald-50 text-emerald-700'}`}>{sent ? 'Sent' : 'Received'}</span>
                  <span className="text-[11px] text-slate-500">{fmtDateTime(c.sent_at)}</span>
                  <span className="text-[12px] font-semibold text-slate-800 flex-1 min-w-[10rem] truncate" title={c.subject || ''}>{c.subject || '(no subject)'}</span>
                  {c.attachment_count > 0 && <span className="text-[10.5px] text-slate-500">📎 {c.attachment_count}</span>}
                  <a href={`${API}/api/mnd/memos/${memoId}/correspondence/${c.id}/file`}
                    className="text-[11px] font-semibold text-orange-700 hover:text-orange-900"
                    title="The email itself (.eml). It opens in your mail program, where Reply all reaches everyone on it and the PCG reply address, so the answer files here.">
                    Open email
                  </a>
                  {(c.reply_all_to || []).length > 0 && (
                    <a href={draft(c, 'reply')} className="text-[11px] font-semibold text-slate-600 hover:text-slate-900"
                      title={`Opens a reply in your mail program to everyone on this email and the PCG reply address, with the whole email below it, so your reply is saved here: ${(c.reply_all_to || []).join(', ')}`}>
                      Reply all
                    </a>
                  )}
                  {(c.reply_all_to || []).length > 0 && (
                    <a href={draft(c, 'follow_up')} className="text-[11px] font-semibold text-slate-600 hover:text-slate-900"
                      title="Opens a new email in your mail program to the same people, with this email below it in full, saved here with every answer to it.">
                      Follow up
                    </a>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate" title={`From ${c.from_email || ''} · To ${(c.to_emails || []).join(', ')}${(c.cc_emails || []).length ? ` · Cc ${c.cc_emails.join(', ')}` : ''}`}>
                  {sent ? `To ${(c.to_emails || []).join(', ')}` : `From ${c.from_name ? `${c.from_name} (${c.from_email})` : c.from_email || ''}`}
                </div>
                {c.excerpt && <div className="text-[11.5px] text-slate-600 mt-1 line-clamp-2">{c.excerpt}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActionRow({ a, comments, me, isCompliance, people, onRefresh, onTeach, mail = [] }) {
  const [editing, setEditing] = useState(false);
  const [editAction, setEditAction] = useState(a.action || '');
  const [editWhat, setEditWhat] = useState(a.what_to_do || '');
  const [editDue, setEditDue] = useState(a.external_due_date ? String(a.external_due_date).slice(0, 10) : '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [newDepts, setNewDepts] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const fileRef = useRef(null);
  const mine = a.mine ?? (a.assignees || []).some((x) => x.user_id === me?.id);
  const st = ACTION_STATUS[a.status] || ACTION_STATUS.pending;
  const actionComments = comments.filter((c) => c.action_id === a.id);

  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const submitReassign = async () => {
    if (!newDepts.length && !newUsers.length) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/mnd/actions/${a.id}/reassign`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ department_ids: newDepts, user_ids: newUsers }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Reassign failed');
      setReassigning(false); setNewDepts([]); setNewUsers([]);
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  const call = async (method, path, body) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/mnd/${path}`, {
        method, credentials: 'include',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Request failed');
      setNote('');
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally { setBusy(false); }
  };

  const uploadEvidence = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    try {
      // Several files in one go: nobody should have to press the button five
      // times to attach five screenshots.
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await fetch(`${API}/api/mnd/actions/${a.id}/evidence`, { method: 'POST', credentials: 'include', body: fd });
        const d = await r.json();
        if (!r.ok) throw new Error(`${f.name}: ${d?.message || 'upload failed'}`);
      }
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className={`px-4 py-3.5 border-b border-slate-100 last:border-b-0 ${mine ? 'bg-orange-50/40 border-l-2 border-l-orange-300' : ''}`}
      title={mine ? 'Assigned to you' : undefined}>
      <div className="flex items-start justify-between gap-4">
        <div className="text-[13.5px] font-semibold text-slate-900 leading-snug">
          {a.n ?? (a.position + 1)}. {a.action}
        </div>
        <div className={`text-xs font-semibold whitespace-nowrap ${st.cls}`}>{st.label}{a.external_due_date ? ` · due ${fmtDate(a.external_due_date)}` : ''}</div>
      </div>
      <div className="mt-2 flex items-center gap-2.5 flex-wrap">
        {a.owner_name ? (
          <span className="inline-flex items-center gap-1.5" title="Owner">
            <Avatar name={a.owner_name} size="w-5.5 h-5.5 w-[22px] h-[22px] text-[9px]" />
            <span className="text-xs font-semibold text-slate-700">{a.owner_name}</span>
          </span>
        ) : (
          <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-rose-100 text-rose-700">No owner — assign someone</span>
        )}
        {(a.assignees || []).filter((x) => x.name !== a.owner_name).length > 0 && (
          <span className="inline-flex items-center -space-x-1.5" title={`With ${(a.assignees || []).filter((x) => x.name !== a.owner_name).map((x) => x.name).join(', ')}`}>
            {(a.assignees || []).filter((x) => x.name !== a.owner_name).slice(0, 5).map((x) => (
              <span key={x.user_id} className="ring-2 ring-white rounded-full inline-flex">
                <Avatar name={x.name} size="w-[22px] h-[22px] text-[9px]" />
              </span>
            ))}
          </span>
        )}
        <span className="text-xs text-slate-500">{a.department_name}</span>
        {(Array.isArray(a.supporting_departments) ? a.supporting_departments : []).map((d) => (
          <span key={d} className="px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 text-[11px] font-semibold">+ {d}</span>
        ))}
        {a.applicability !== 'mandatory' ? <span className="text-[11px] text-amber-700 font-semibold">{a.applicability}</span> : null}
        {isCompliance && (
          <button onClick={() => {
            if (!reassigning) {
              // Open pre-loaded with TODAY'S assignment, so adding a person is
              // one click and nobody silently drops off. Owner stays first
              // (first pick = owner); removing someone is an explicit unclick.
              const deptIds = [];
              if (a.department_id) deptIds.push(a.department_id);
              for (const name of (Array.isArray(a.supporting_departments) ? a.supporting_departments : [])) {
                const d = (people?.departments || []).find((x) => x.name === name);
                if (d && !deptIds.includes(d.id)) deptIds.push(d.id);
              }
              // The WHOLE current roster, owner first — so unclicking a
              // person truly removes them (the server drops unpicked people
              // even when department fan-out would re-add them) and clicking
              // adds. The server records only the delta as the lesson, never
              // a roster snapshot.
              const roster = [];
              if (a.owner_user_id) roster.push(a.owner_user_id);
              for (const x of (a.assignees || [])) {
                if (x.user_id && !roster.includes(x.user_id)) roster.push(x.user_id);
              }
              setNewDepts(deptIds);
              setNewUsers(roster);
            }
            setReassigning((v) => !v);
          }} className="text-[11px] font-semibold text-orange-700 hover:text-orange-900">
            {reassigning ? 'Cancel' : 'Reassign'}
          </button>
        )}
        {isCompliance && onTeach && (
          <button onClick={onTeach} className="text-[11px] font-semibold text-violet-700 hover:text-violet-900">
            Teach the agent
          </button>
        )}
      </div>
      {reassigning && (
        <div className="mt-2 bg-orange-50/60 border border-orange-200 rounded-lg px-3 py-2.5">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            Departments — first pick is accountable, the rest support
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(people?.departments || []).map((d) => {
              const idx = newDepts.indexOf(d.id);
              const on = idx >= 0;
              return (
                <button key={d.id} onClick={() => toggle(newDepts, setNewDepts, d.id)}
                  className={`px-2.5 py-1 rounded-lg text-[11.5px] font-semibold border ${on
                    ? (idx === 0 ? 'bg-orange-500 text-white border-orange-500' : 'bg-sky-100 text-sky-800 border-sky-300')
                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>
                  {d.name}{on ? (idx === 0 ? ' · accountable' : ' · supporting') : ''}
                </button>
              );
            })}
          </div>
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            People — first pick is the owner, everyone picked is assigned, unclick to remove someone
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(people?.users || [])
              .filter((u) => newUsers.includes(u.id) /* anyone picked stays visible, or they could never be unclicked */
                || !newDepts.length || newDepts.includes(u.department_id) || !u.department_id
                || ['compliance_officer', 'compliance_analyst'].includes(u.user_type))
              .map((u) => {
                const idx = newUsers.indexOf(u.id);
                const on = idx >= 0;
                return (
                  <button key={u.id} onClick={() => toggle(newUsers, setNewUsers, u.id)}
                    className={`px-2.5 py-1 rounded-lg text-[11.5px] font-semibold border ${on
                      ? (idx === 0 ? 'bg-slate-800 text-white border-slate-800' : 'bg-slate-200 text-slate-800 border-slate-400')
                      : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>
                    {u.name}{on ? (idx === 0 ? ' · owner' : '') : ''}
                  </button>
                );
              })}
          </div>
          <div className="flex items-center gap-2">
            <button disabled={busy || (!newDepts.length && !newUsers.length)} onClick={submitReassign}
              className="px-3.5 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">Move it</button>
            <span className="text-[10.5px] text-slate-500">The agent learns from every choice you make here.</span>
          </div>
        </div>
      )}
      {a.applies_if ? (
        <div className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          Applies only if: {a.applies_if}
        </div>
      ) : null}
      {a.what_to_do ? <div className="text-xs text-slate-600 mt-2 leading-relaxed whitespace-pre-wrap">{a.what_to_do}</div> : null}
      {a.source_quote ? (
        <div className="text-[11.5px] text-slate-500 mt-2 italic">
          Memo says: "{a.source_quote}"{a.source_page ? ` (p. ${a.source_page})` : ''}
        </div>
      ) : null}
      {a.evidence_hint ? <div className="text-[11.5px] text-slate-500 mt-1">Evidence that closes this: {a.evidence_hint}</div> : null}

      {(a.evidence || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {a.evidence.map((e) => {
            // Everything here opens. Emails (.html) and PDFs render inline in
            // a new tab; anything else downloads. A record you cannot read is
            // not a record.
            const inline = /pdf|^text\/html|^image\//i.test(e.mime_type || '') ? '?inline=1' : '';
            return (
              <a key={e.id}
                href={`${API}/api/mnd/actions/${a.id}/evidence/${e.id}/file${inline}`}
                target="_blank" rel="noreferrer"
                title={e.note || e.file_name}
                className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-medium hover:bg-emerald-100 hover:underline cursor-pointer">
                📎 {e.file_name}
              </a>
            );
          })}
        </div>
      )}

      <CorrespondencePanel memoId={a.memo_id} rows={mail} compact />

      {/* Per-action discussion — the conversation about this obligation lives
          HERE, on the action itself, never in a general pile at the bottom.
          Anyone on the action (or compliance) can write. */}
      {(actionComments.length > 0 || mine || isCompliance) && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
          <CommentThread comments={actionComments} />
          {(mine || isCompliance) && (
            <ActionCommentBox actionId={a.id} memoId={a.memo_id} meName={me?.name || me?.email} onPosted={onRefresh} />
          )}
        </div>
      )}

      {a.decision_note && a.status === 'returned' ? (
        <div className="mt-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-800">
          Returned: {a.decision_note}
        </div>
      ) : null}

      <div className="mt-2.5 flex items-center gap-2 flex-wrap">
        {/* Evidence can be attached by the people on the action while it is
            open, and by compliance at any point before the memo is retired —
            the reviewer holding a document with nowhere to put it was a hole. */}
        {(mine || isCompliance) && a.status !== 'approved' && (
          <>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={uploadEvidence} />
            <button disabled={busy} onClick={() => fileRef.current?.click()}
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-50">
              {busy ? 'Attaching…' : 'Attach evidence'}
            </button>
          </>
        )}
        {isCompliance && a.status === 'approved' && (
          <>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={uploadEvidence} />
            <button disabled={busy} onClick={() => fileRef.current?.click()}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-400 text-xs font-medium hover:bg-slate-50 hover:text-slate-600">
              {busy ? 'Attaching…' : 'Add evidence to the record'}
            </button>
          </>
        )}
        {mine && ['pending', 'in-progress', 'returned'].includes(a.status) && (
          <>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short note to the reviewer (optional)"
              className="flex-1 min-w-[160px] px-3 py-1.5 rounded-lg border border-slate-200 text-xs" />
            <button disabled={busy} onClick={() => call('PATCH', `actions/${a.id}/submit`, { note })}
              className="px-3.5 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold">
              Submit for review
            </button>
          </>
        )}
        {isCompliance && a.status === 'approved' && (
          <button disabled={busy} onClick={async () => {
            const why = window.prompt('Reopen this approved action? Tell the owner why:');
            if (!why) return;
            await call('PATCH', `actions/${a.id}/reopen`, { note: why });
          }} className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-50">
            Reopen
          </button>
        )}
        {isCompliance && a.status === 'submitted' && (
          <>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Decision note (required to return)"
              className="flex-1 min-w-[160px] px-3 py-1.5 rounded-lg border border-slate-200 text-xs" />
            <button disabled={busy} onClick={() => call('PATCH', `actions/${a.id}/approve`, { note })}
              className="px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold">
              Approve
            </button>
            <button disabled={busy} onClick={() => call('PATCH', `actions/${a.id}/return`, { note })}
              className="px-3.5 py-1.5 rounded-lg border border-rose-300 text-rose-700 text-xs font-semibold hover:bg-rose-50">
              Return
            </button>
          </>
        )}
        {/* Compliance can resolve an action without the submit cycle: close
            it as done, or record that it does not apply to this plan. Both
            demand a written reason — the reason IS the record. */}
        {isCompliance && !['approved', 'submitted'].includes(a.status) && (
          <>
            <button disabled={busy} onClick={async () => {
              const why = window.prompt('Close this action as done. Why is the work complete?');
              if (!why) return;
              await call('PATCH', `actions/${a.id}/close`, { resolution: 'done', note: why });
            }} className="px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 text-xs font-semibold hover:bg-emerald-50">
              Close as done
            </button>
            <button disabled={busy} onClick={async () => {
              const why = window.prompt('Mark this action not applicable. Why does it not apply to this plan?');
              if (!why) return;
              await call('PATCH', `actions/${a.id}/close`, { resolution: 'not-applicable', note: why });
            }} className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-50">
              Not applicable
            </button>
          </>
        )}
        {isCompliance && a.status !== 'approved' && (
          <button disabled={busy} onClick={() => setEditing((v) => !v)}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-50">
            {editing ? 'Cancel edit' : 'Edit'}
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-2.5 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-3 space-y-2">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">Action</div>
            <input value={editAction} onChange={(e) => setEditAction(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-xs font-semibold" />
          </div>
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">How to do it</div>
            <textarea value={editWhat} onChange={(e) => setEditWhat(e.target.value)} rows={3}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-xs leading-relaxed resize-y" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">Due date</div>
              <input type="date" value={editDue} onChange={(e) => setEditDue(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs" />
            </div>
            <div className="flex-1" />
            <button disabled={busy || !editAction.trim()} onClick={async () => {
              await call('PATCH', `actions/${a.id}/edit`, {
                action: editAction.trim(),
                what_to_do: editWhat,
                external_due_date: editDue || null,
              });
              setEditing(false);
            }} className="px-3.5 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold disabled:opacity-40 self-end">
              Save changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Memo level ownership. An informational memo has no actions, so without this
 * nobody is accountable and nothing can close it. One named owner, the
 * department, who it went to, and the acknowledgement that closes it.
 */
function MemoOwnerBar({ memo, isCompliance, people, onRefresh }) {
  const [assigning, setAssigning] = useState(false);
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ackNote, setAckNote] = useState('');
  const recipients = Array.isArray(memo.recipients) ? memo.recipients : [];
  const acked = recipients.filter((r) => r.acknowledged_at).length;
  const mine = memo.my_acknowledgement;
  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const save = async () => {
    if (!depts.length && !users.length) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/mnd/memos/${memo.id}/assign`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ department_ids: depts, user_ids: users }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Assignment failed');
      setAssigning(false); setDepts([]); setUsers([]);
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  const acknowledge = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/mnd/memos/${memo.id}/acknowledge`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: ackNote }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Acknowledgement failed');
      setAckNote('');
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${memo.owner_name ? 'bg-slate-800 text-white' : 'bg-rose-100 text-rose-700'}`}>
          {memo.owner_name ? `Memo owner: ${memo.owner_name}` : 'NO MEMO OWNER — assign someone'}
        </span>
        {memo.owner_department_name ? <span className="text-xs text-slate-500">{memo.owner_department_name}</span> : null}
        {recipients.length > 0 ? (
          <span className="text-[11px] text-slate-400">read by {acked} of {recipients.length}</span>
        ) : null}
        {isCompliance && (
          <button onClick={() => setAssigning((v) => !v)} className="text-[11px] font-semibold text-orange-700 hover:text-orange-900">
            {assigning ? 'Cancel' : (memo.owner_name ? 'Reassign memo' : 'Assign memo')}
          </button>
        )}
      </div>

      {assigning && (
        <div className="mt-2 bg-orange-50/60 border border-orange-200 rounded-lg px-3 py-2.5">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            Departments — first pick is accountable, the rest are copied in
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(people?.departments || []).map((d) => {
              const idx = depts.indexOf(d.id); const on = idx >= 0;
              return (
                <button key={d.id} onClick={() => toggle(depts, setDepts, d.id)}
                  className={`px-2.5 py-1 rounded-lg text-[11.5px] font-semibold border ${on
                    ? (idx === 0 ? 'bg-orange-500 text-white border-orange-500' : 'bg-sky-100 text-sky-800 border-sky-300')
                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>
                  {d.name}{on ? (idx === 0 ? ' · accountable' : ' · copied') : ''}
                </button>
              );
            })}
          </div>
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            People — first pick owns the memo, everyone picked must acknowledge
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(people?.users || [])
              .filter((u) => !depts.length || depts.includes(u.department_id) || !u.department_id
                || ['compliance_officer', 'compliance_analyst'].includes(u.user_type))
              .map((u) => {
                const idx = users.indexOf(u.id); const on = idx >= 0;
                return (
                  <button key={u.id} onClick={() => toggle(users, setUsers, u.id)}
                    className={`px-2.5 py-1 rounded-lg text-[11.5px] font-semibold border ${on
                      ? (idx === 0 ? 'bg-slate-800 text-white border-slate-800' : 'bg-slate-200 text-slate-800 border-slate-400')
                      : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>
                    {u.name}{on && idx === 0 ? ' · owner' : ''}
                  </button>
                );
              })}
          </div>
          <div className="flex items-center gap-2">
            <button disabled={busy || (!depts.length && !users.length)} onClick={save}
              className="px-3.5 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">Assign it</button>
            <span className="text-[10.5px] text-slate-500">The agent learns from every choice you make here.</span>
          </div>
        </div>
      )}

      {/* Who has read it: every recipient by name, with the date and any note.
          Compliance reads this instead of a bare count. */}
      {recipients.length > 0 && (
        <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
            Read and acknowledged · {acked} of {recipients.length}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {recipients.map((r) => (
              <span key={r.user_id} title={r.acknowledgement || ''}
                className={`px-2.5 py-1 rounded-lg text-[11.5px] font-semibold border ${r.acknowledged_at
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  : 'bg-white text-slate-500 border-slate-200'}`}>
                {r.name}{r.acknowledged_at ? ` · ${fmtDateTime(r.acknowledged_at)}` : ' · not yet'}
              </span>
            ))}
          </div>
          {recipients.some((r) => r.acknowledgement) && (
            <div className="mt-2 space-y-0.5">
              {recipients.filter((r) => r.acknowledgement).map((r) => (
                <div key={`n-${r.user_id}`} className="text-[11.5px] text-slate-600">
                  <span className="font-semibold text-slate-800">{r.name}:</span> {r.acknowledgement}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Informational memos close by acknowledgement, not by actions. */}
      {mine && !mine.acknowledged_at && (
        <div className="mt-2 bg-blue-50/70 border border-blue-200 rounded-lg px-3 py-2.5">
          <div className="text-xs text-slate-700 mb-1.5 font-semibold">This memo needs your acknowledgement.</div>
          <div className="flex gap-2">
            <input value={ackNote} onChange={(e) => setAckNote(e.target.value)}
              placeholder="Optional: what you did with it, or who you passed it to"
              className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs" />
            <button disabled={busy} onClick={acknowledge}
              className="px-3.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold whitespace-nowrap">
              I have read this
            </button>
          </div>
        </div>
      )}
      {mine && mine.acknowledged_at && (
        <div className="mt-2 text-[11.5px] text-emerald-700 font-semibold">
          You acknowledged this on {fmtDate(mine.acknowledged_at)}.
        </div>
      )}
    </div>
  );
}

/**
 * Compliance changes an informational memo to actionable: one action, named
 * assignees, in one stroke. The agent learns the decision and confirms in the
 * discussion what it will do the next time a memo like this arrives.
 */
function MakeActionablePanel({ memo, people, onDone, onCancel }) {
  const [action, setAction] = useState('');
  const [whatToDo, setWhatToDo] = useState('');
  const [due, setDue] = useState('');
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setUsers((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const save = async () => {
    if (action.trim().length < 3 || !users.length) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/mnd/memos/${memo.id}/make-actionable`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: action.trim(), what_to_do: whatToDo.trim(), external_due_date: due || null, user_ids: users }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Could not make the memo actionable');
      onDone();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2 bg-violet-50/60 border border-violet-200 rounded-lg px-3 py-2.5">
      <div className="text-xs font-semibold text-slate-700 mb-1.5">
        Change this memo to actionable — describe the action and pick who does it.
      </div>
      <input value={action} onChange={(e) => setAction(e.target.value)}
        placeholder="The action, e.g. Update the enrollment letters to the new model notices"
        className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-xs mb-1.5" />
      <textarea value={whatToDo} onChange={(e) => setWhatToDo(e.target.value)} rows={2}
        placeholder="Optional: what doing it looks like — the how, the evidence expected"
        className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-xs mb-1.5" />
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500">Due</span>
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
          className="px-2 py-1 rounded-lg border border-slate-200 text-xs" />
      </div>
      <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">
        People — first pick owns the action, everyone picked is assigned
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(people?.users || []).map((u) => {
          const idx = users.indexOf(u.id); const on = idx >= 0;
          return (
            <button key={u.id} onClick={() => toggle(u.id)}
              className={`px-2.5 py-1 rounded-lg text-[11.5px] font-semibold border ${on
                ? (idx === 0 ? 'bg-slate-800 text-white border-slate-800' : 'bg-slate-200 text-slate-800 border-slate-400')
                : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>
              {u.name}{on && idx === 0 ? ' · owner' : ''}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <button disabled={busy || action.trim().length < 3 || !users.length} onClick={save}
          className="px-3.5 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold disabled:opacity-50">
          Make it actionable
        </button>
        <button onClick={onCancel} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
        <span className="text-[10.5px] text-slate-500">The agent learns this decision and confirms it in the discussion.</span>
      </div>
    </div>
  );
}

function MemoView({ memoId, me, isCompliance, people, onBack }) {
  const [data, setData] = useState(null);
  // Declared up here with the other hooks: everything below the loading guard
  // runs conditionally, and a hook down there would change order once the
  // memo arrives.
  const [exporting, setExporting] = useState(false);
  // null = closed; 'global' or an action id = open with that scope.
  const [talkScope, setTalkScope] = useState(null);
  const [makingActionable, setMakingActionable] = useState(false);
  const load = useCallback(() => {
    fetch(`${API}/api/mnd/memos/${memoId}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (d?.success) setData(d); })
      .catch(() => {});
  }, [memoId]);
  useEffect(() => {
    load();
    const t = setInterval(() => {
      // Poll only while the analyzer is running.
      setData((cur) => {
        if (cur?.memo?.status === 'analyzing') load();
        return cur;
      });
    }, 5000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return <div className="p-8 text-sm text-slate-400">Loading memo…</div>;
  const { memo, actions, comments, proposals: memoProposals, documents = [], correspondence = [] } = data;
  // What this person sees: their own actions first, numbered from 1, then the
  // rest in file order. The PDF export keeps the file order.
  const myActions = orderActionsForViewer(actions, me?.id);
  const memoComments = comments.filter((c) => !c.action_id);
  // Emails linked to no action stand on the memo: all of them on an
  // informational memo, the memo level chases on an actionable one.
  const memoMail = correspondence.filter((c) => !(c.action_ids || []).length);
  const openProposals = (memoProposals || []).filter((p) => p.status === 'proposed');

  const decideMemoProposal = async (id, decision) => {
    const r = await fetch(`${API}/api/mnd/proposals/${id}/${decision}`, { method: 'PATCH', credentials: 'include' });
    const d = await r.json().catch(() => ({}));
    if (r.ok && d?.created) alert(`Created: ${d.created}`);
    load();
  };

  const checkHandoffs = async () => {
    await fetch(`${API}/api/mnd/memos/${memo.id}/proposals/generate`, { method: 'POST', credentials: 'include' });
    load();
  };

  const mintAuditorLink = async () => {
    const r = await fetch(`${API}/api/mnd/memos/${memo.id}/evidence-pack`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    const d = await r.json();
    if (!r.ok) return alert(d?.message || 'Could not create the link');
    try { await navigator.clipboard.writeText(d.url); } catch { /* clipboard blocked */ }
    window.prompt(`Read only auditor link (expires in ${d.expires_in_days} days). It was copied to your clipboard:`, d.url);
  };

  const retireMemo = async () => {
    const note = window.prompt('Retire this memo? It is never deleted; it leaves the open queues. Optional note:');
    if (note === null) return;
    await fetch(`${API}/api/mnd/memos/${memo.id}/retire`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    load();
  };

  // Informational memos (no actions) can be closed by compliance directly —
  // the written reason is the record (operator order 2026-08-19). Memos with
  // actions complete through their actions, never from here.
  const closeMemo = async () => {
    const note = window.prompt('Close this memo as complete. Why is the matter finished? That sentence is the record:');
    if (!note || !note.trim()) return;
    const r = await fetch(`${API}/api/mnd/memos/${memo.id}/close`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note.trim() }),
    });
    const d = await r.json();
    if (!r.ok) return alert(d?.message || 'Could not close the memo');
    load();
  };

  const unretireMemo = async () => {
    await fetch(`${API}/api/mnd/memos/${memo.id}/unretire`, { method: 'PATCH', credentials: 'include' });
    load();
  };

  // The memo record as a branded PDF. Everything on this screen goes into it,
  // including the discussion, because the work is always visible here.
  const exportPdf = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { exportMemoPdf } = await import('./exportMemoPdf');
      await exportMemoPdf({ memo, actions, comments, documents }, { generatedBy: me?.name || me?.email || '' });
    } catch (err) {
      alert(`Could not build the PDF: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700">← Back to memos</button>
      <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-400 font-mono">{memo.memo_number}</span>
              <Pill map={CLS_PILL} value={memo.classification} />
              <Pill map={STATUS_PILL} value={memo.status} label={STATUS_LABEL[memo.status]} />
              {isCompliance && memo.classification === 'Informational' && !memo.retired_at && (
                <button onClick={() => setMakingActionable((v) => !v)}
                  className="text-[11px] font-semibold text-violet-700 hover:text-violet-900">
                  {makingActionable ? 'Cancel' : 'Make actionable'}
                </button>
              )}
            </div>
            <div className="text-lg font-bold text-slate-900 mt-1.5">{memo.title}</div>
            <div className="text-xs text-slate-500 mt-1">
              Received {fmtDate(memo.received_date) || 'not stated'} · Due {fmtDate(memo.external_due_date) || 'none stated'} · Urgency {memo.urgency}
            </div>
            {makingActionable && (
              <MakeActionablePanel memo={memo} people={people}
                onDone={() => { setMakingActionable(false); load(); }}
                onCancel={() => setMakingActionable(false)} />
            )}
            <MemoOwnerBar memo={memo} isCompliance={isCompliance} people={people} onRefresh={load} />
            {documents.length > 0 && (
              <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                <span className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mr-0.5">
                  Documents · {documents.length}
                </span>
                {documents.map((d) => {
                  // A sent Workstream email is filed as the email itself
                  // (.eml): it opens in the reader's mail program, where
                  // Reply all reaches everyone on it and the PCG reply
                  // address, so the answer files back on this memo.
                  const isEmail = /^message\/rfc822/i.test(d.mime_type || '');
                  const inline = !isEmail && /pdf|^text\/html|^image\//i.test(d.mime_type || '');
                  return (
                  <a key={d.id}
                    href={`${API}/api/mnd/memos/${memo.id}/documents/${d.id}/file${inline ? '?inline=1' : ''}`}
                    target="_blank" rel="noreferrer"
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11.5px] font-medium hover:bg-slate-50 ${
                      d.kind === 'source' ? 'border-orange-200 text-orange-800 bg-orange-50/60' : 'border-slate-200 text-slate-600 bg-white'}`}
                    title={isEmail
                      ? `${d.file_name} · opens in your mail program. Reply all reaches everyone on the email and the PCG reply address, so replies file on this memo.`
                      : `${d.file_name} · ${d.size_bytes ? `${Math.max(1, Math.round(d.size_bytes / 1024))} KB` : ''}${d.kind === 'source' ? ' · analysed source' : ''}`}>
                    <span aria-hidden>{isEmail ? '✉️' : '📄'}</span>
                    <span className="max-w-[220px] truncate">{d.file_name}</span>
                    {d.kind === 'source' && <span className="text-[9.5px] font-bold uppercase tracking-wide text-orange-500">source</span>}
                    {isEmail && <span className="text-[9.5px] font-bold uppercase tracking-wide text-slate-400">email</span>}
                  </a>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 whitespace-nowrap">
            <a href={`${API}/api/mnd/memos/${memo.id}/file?inline=1`} target="_blank" rel="noreferrer"
              className="text-xs font-semibold text-orange-700 hover:text-orange-900">
              Open source document ↗
            </a>
            {isCompliance && (
              <div className="flex items-center gap-2.5">
                <button onClick={exportPdf} disabled={exporting}
                  className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-50">
                  {exporting ? 'Building PDF…' : 'Print / PDF'}
                </button>
                <button onClick={mintAuditorLink} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-800">Auditor link</button>
                {actions.length === 0 && ['open', 'pending-review'].includes(memo.status) && (
                  <button onClick={closeMemo}
                    title="Close this informational memo as complete — the written reason becomes the record. Outstanding acknowledgements stop being owed."
                    className="text-[11.5px] font-semibold text-emerald-700 hover:text-emerald-900">
                    Close memo
                  </button>
                )}
                {memo.status !== 'retired' ? (
                  <button onClick={retireMemo} className="text-[11.5px] font-semibold text-rose-600 hover:text-rose-800">Retire</button>
                ) : (
                  <button onClick={unretireMemo} className="text-[11.5px] font-semibold text-emerald-700 hover:text-emerald-900">Unretire</button>
                )}
              </div>
            )}
          </div>
        </div>

        {memo.status === 'analyzing' ? (
          <div className="mt-4 px-4 py-3 bg-violet-50 border border-violet-200 rounded-xl text-sm text-violet-800">
            The agent is reading this memo: briefing, verified quotes, and routed actions will appear here in about a minute.
          </div>
        ) : null}
        {memo.status === 'failed' ? (
          <div className="mt-4 px-4 py-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-800">
            Analysis failed: {memo.error_message || 'unknown error'}. Upload the memo again or contact support.
          </div>
        ) : null}

        {memo.briefing?.headline ? (
          <div className="mt-4 px-4 py-3 bg-slate-50 rounded-xl text-[13px] text-slate-700 leading-relaxed">
            <span className="font-semibold text-slate-900">Agent briefing:</span> {memo.briefing.headline}
            {memo.briefing.whatItMeans ? ` ${memo.briefing.whatItMeans}` : ''}
            {memo.analysis_meta?.groundedActions != null ? (
              <span className="text-slate-400"> Every quote below was verified against the memo text.</span>
            ) : null}
          </div>
        ) : null}

        {memo.briefing?.enforcementRisk ? (
          <div className="mt-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
            If this is missed: {memo.briefing.enforcementRisk}
          </div>
        ) : null}

        {Array.isArray(memo.summary) && memo.summary.length > 0 && (
          <div className="mt-3 text-[13px] text-slate-700 leading-relaxed space-y-1">
            {memo.summary.map((s, i) => <div key={i}>{s}</div>)}
          </div>
        )}

        {isCompliance && openProposals.length > 0 && (
          <div className="mt-4 bg-violet-50/60 border border-violet-200 rounded-xl p-3.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-violet-700 mb-2">
              Agent proposals for this memo
            </div>
            <div className="flex gap-3 flex-wrap">
              {openProposals.map((p) => <ProposalCard key={p.id} p={p} onDecide={decideMemoProposal} />)}
            </div>
          </div>
        )}
        {isCompliance && memo.status !== 'analyzing' && openProposals.length === 0 && (
          <button onClick={checkHandoffs} className="mt-3 text-[11.5px] font-semibold text-violet-700 hover:text-violet-900">
            Ask the agent to check for hand-offs (calendar dates, policy impact, risk)
          </button>
        )}
        {isCompliance && (
          <div className="mt-3">
            <button onClick={() => setTalkScope('global')}
              className="px-3.5 py-1.5 rounded-lg bg-white border border-orange-300 hover:border-orange-500 text-orange-700 text-xs font-semibold">
              Talk to the agent
            </button>
          </div>
        )}
        <TalkToAgentModal open={talkScope != null} onClose={() => { setTalkScope(null); load(); }}
          actions={myActions} presetActionId={talkScope && talkScope !== 'global' ? talkScope : null} />

        {actions.length > 0 && (
          <div className="mt-4 border border-slate-200 rounded-xl overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              Actions · {actions.filter((a) => a.status === 'approved').length} of {actions.length} approved
            </div>
            {myActions.map((a) => (
              <ActionRow key={a.id} a={a} comments={comments} me={me} isCompliance={isCompliance} people={people} onRefresh={load} onTeach={() => setTalkScope(a.id)}
                mail={correspondence.filter((c) => (c.action_ids || []).includes(a.id))} />
            ))}
          </div>
        )}

        {/* Work talk lives on each action, but a memo always has a thread of
            its own: an informational memo has no actions at all, and without
            this nobody on it could say a word. */}
        {/* Conversation belongs on the actions. Only two things ever render
            here: the discussion of a memo that HAS no actions, and history
            carried over from the previous app, which was memo-level by
            nature and stays visible read-only. */}
        {(actions.length === 0 || memoMail.length > 0) && (
          <CorrespondencePanel memoId={memo.id} rows={memoMail} title={actions.length === 0 ? 'Correspondence' : 'Correspondence on the memo'} />
        )}

        {actions.length === 0 ? (
          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2.5">Discussion</div>
            <div className="space-y-3">
              <CommentThread comments={memoComments} />
              <ActionCommentBox memoId={memo.id} meName={me?.name || me?.email} onPosted={load} />
            </div>
          </div>
        ) : memoComments.length > 0 ? (
          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2.5">
              Carried over from the previous Mandate
            </div>
            <CommentThread comments={memoComments} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// My Work
// ---------------------------------------------------------------------------
function MyWork({ onOpen }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/mnd/my-work`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setItems(d?.items || [])).catch(() => setItems([]));
  }, []);
  if (!items) return <div className="p-6 text-sm text-slate-400">Loading…</div>;
  if (!items.length) return <div className="mt-4 bg-white border border-slate-200 rounded-2xl px-4 py-10 text-center text-sm text-slate-400">Nothing needs you right now.</div>;
  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
      {items.map((it, i) => (
        <button key={it.id || `memo-${it.memo_id}-${i}`} onClick={() => onOpen(it.memo_id)}
          className="w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50/70 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-semibold text-slate-900 truncate">{it.action}</div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">{it.memo_number} · {it.memo_title}</div>
          </div>
          {it.status === 'returned' ? (
            <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold whitespace-nowrap">RETURNED, FIX FIRST</span>
          ) : it.status === 'to-acknowledge' ? (
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold whitespace-nowrap">READ AND ACKNOWLEDGE</span>
          ) : null}
          <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">{it.external_due_date ? `Due ${fmtDate(it.external_due_date)}` : ''}</div>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

// Due date for a memo row: the earliest date among action items still open.
// Rows without any action items yet show the memo's own date; rows whose
// items are all closed show nothing, because nothing is pending.
function openDueDate(m) {
  if (m.open_action_count === undefined) return m.external_due_date;
  if (m.open_action_count > 0) return m.open_due_date || m.external_due_date;
  return (m.action_count || 0) > 0 ? null : m.external_due_date;
}

export default function MandateNative({ user }) {
  // Department users land on their own work; Command is the org wide view.
  const landsOnCommand = (() => {
    const ut = user?.user_type || user?.effective_user_type || null;
    return (ut && ['platform_superuser', 'compliance_officer', 'compliance_analyst'].includes(ut))
      || (!ut && (['superadmin'].includes(String(user?.role || '').toLowerCase())
          || (['admin', 'associate'].includes(String(user?.role || '').toLowerCase()) && !user?.department_id)));
  })();
  const [tab, setTab] = useState(landsOnCommand ? 'command' : 'mywork');
  const [memos, setMemos] = useState([]);
  const [memoCounts, setMemoCounts] = useState(null);
  const [closed, setClosed] = useState([]);
  // Filters are server-side (see FilterBar). Open-list filters live in the
  // URL so a filtered list survives refresh and can be handed to a colleague;
  // the closed archive keeps its filters per visit. `listMemos` is the memos
  // tab's own result set — the Command tab's bottom list keeps reading the
  // unfiltered `memos` and must never be narrowed by this state.
  const [openFilters, setOpenFilters] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const f = { ...EMPTY_FILTERS };
      for (const k of Object.keys(f)) { const v = p.get(`mnd_${k}`); if (v) f[k] = v; }
      return f;
    } catch { return { ...EMPTY_FILTERS }; }
  });
  const [listMemos, setListMemos] = useState(null);
  const [closedFilters, setClosedFilters] = useState({ ...EMPTY_FILTERS });
  const [dashboard, setDashboard] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [people, setPeople] = useState(null);
  const [openMemoId, setOpenMemoId] = useState(null);

  const isCompliance = (() => {
    const ut = user?.user_type || user?.effective_user_type || null;
    return (ut && ['platform_superuser', 'compliance_officer', 'compliance_analyst'].includes(ut))
      || (!ut && (['superadmin'].includes(String(user?.role || '').toLowerCase())
          || (['admin', 'associate'].includes(String(user?.role || '').toLowerCase()) && !user?.department_id)));
  })();

  const loadAll = useCallback(() => {
    fetch(`${API}/api/mnd/memos`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => { if (d?.success) { setMemos(d.memos); setMemoCounts(d.counts || null); } }).catch(() => {});
    if (isCompliance) {
      fetch(`${API}/api/mnd/command`, { credentials: 'include' })
        .then((r) => r.json()).then((d) => { if (d?.success) setDashboard(d); }).catch(() => {});
    }
    if (isCompliance) {
      fetch(`${API}/api/mnd/proposals`, { credentials: 'include' })
        .then((r) => r.json()).then((d) => { if (d?.success) setProposals(d.proposals); }).catch(() => {});
      fetch(`${API}/api/mnd/org-people`, { credentials: 'include' })
        .then((r) => r.json()).then((d) => { if (d?.success) setPeople(d); }).catch(() => {});
    }
  }, [isCompliance]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // One query-string builder for both lists: only non-empty fields become
  // params, so an untouched filter bar produces the same request the page
  // has always made.
  const filterQS = (f, extra = '') => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) { const t = String(v || '').trim(); if (t) p.set(k, t); }
    const s = p.toString();
    return extra + (s ? (extra ? '&' : '?') + s : '');
  };

  // Open list: re-queried as filters change (250ms debounce — the audit chain
  // logs every list read, so no per-keystroke fetches). Filters mirror into
  // the URL so the view is shareable and survives refresh.
  useEffect(() => {
    if (tab !== 'memos') return;
    const t = setTimeout(() => {
      fetch(`${API}/api/mnd/memos${filterQS(openFilters)}`, { credentials: 'include' })
        .then((r) => r.json()).then((d) => { if (d?.success) { setListMemos(d.memos); setMemoCounts(d.counts || null); } }).catch(() => {});
      try {
        const url = new URL(window.location.href);
        for (const [k, v] of Object.entries(openFilters)) {
          const t2 = String(v || '').trim();
          if (t2) url.searchParams.set(`mnd_${k}`, t2); else url.searchParams.delete(`mnd_${k}`);
        }
        window.history.replaceState(null, '', url.toString());
      } catch { /* URL mirroring is best-effort */ }
    }, 250);
    return () => clearTimeout(t);
  }, [tab, openFilters]);

  // Closed history is loaded only when that tab is open, and re-queried as
  // the operator types, so the archive stays searchable without ever mixing
  // into the live queues.
  useEffect(() => {
    if (tab !== 'closed') return;
    const t = setTimeout(() => {
      fetch(`${API}/api/mnd/memos${filterQS(closedFilters, '?view=closed')}`, { credentials: 'include' })
        .then((r) => r.json()).then((d) => { if (d?.success) setClosed(d.memos); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [tab, closedFilters]);

  // Deep links from digests and @mention emails: ?app=mandate_native&mndMemo=<id>
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('mndMemo');
      if (p) { setOpenMemoId(p); setTab('memos'); }
    } catch { /* no deep link */ }
  }, []);

  const decideProposal = async (id, decision) => {
    await fetch(`${API}/api/mnd/proposals/${id}/${decision}`, { method: 'PATCH', credentials: 'include' });
    loadAll();
  };

  const openMemo = (id) => { setOpenMemoId(id); setTab('memos'); };

  // Department options come from what the lists have actually shown — the
  // same two sources as the row chips (action departments, owner department).
  const deptOptions = useMemo(() => [...new Set(
    [...memos, ...(listMemos || []), ...closed].flatMap((m) => {
      const ds = Array.isArray(m.departments) ? m.departments : [];
      return [...ds, m.owner_department_name].filter(Boolean);
    })
  )].sort(), [memos, listMemos, closed]);

  return (
    <div className="min-h-full bg-slate-100/60">
      {/* App header — matches the platform pattern */}
      <div className="bg-white px-8 pt-5 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#F58220,#f59e0b)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <path d="M3 8l9-5 9 5v8l-9 5-9-5z" /><path d="M12 3v18" />
              </svg>
            </div>
            <div className="text-[21px] font-bold text-slate-900 tracking-tight">Precision Mandate</div>
          </div>
          {isCompliance && <UploadButton onDone={(id) => { loadAll(); openMemo(id); }} />}
        </div>
        <div className="flex gap-1.5 mt-4">
          {[
            // Command is the org wide view: compliance only. A department
            // user lands on My Work, which is their work and nobody else's.
            ...(isCompliance ? [['command', 'Command']] : []),
            ['mywork', 'My Work'],
            ['memos', `${isCompliance ? 'Open memos' : 'My memos'}${memoCounts ? ` (${memoCounts.open_count})` : ''}${memos.some((m) => m.new_activity) ? ` · ${memos.filter((m) => m.new_activity).length} new` : ''}`],
            ['closed', `Closed${memoCounts ? ` (${memoCounts.closed_count})` : ''}`],
          ].map(([k, l]) => (
            <button key={k} onClick={() => { setTab(k); setOpenMemoId(null); if (k !== 'mywork') loadAll(); }}
              className={`px-4.5 py-2.5 px-5 text-[13.5px] rounded-t-xl ${tab === k
                ? 'bg-slate-50 border border-b-0 border-slate-200 font-bold text-slate-900'
                : 'text-slate-500 hover:text-slate-700'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-8 py-6">
        {openMemoId ? (
          <MemoView memoId={openMemoId} me={user} isCompliance={isCompliance} people={people} onBack={() => { setOpenMemoId(null); loadAll(); }} />
        ) : tab === 'command' ? (
          <>
            <CommandBand counts={dashboard?.counts} />
            <ProposalsStrip proposals={proposals} onDecide={decideProposal} />
            <div className="mt-4 flex gap-3.5 flex-wrap items-start">
              <ActionQueue title="Overdue" tone="overdue" rows={dashboard?.overdue} onOpen={openMemo} />
              <ActionQueue title="Due in 14 days" tone="soon" rows={dashboard?.due_soon} onOpen={openMemo} />
            </div>
            <AckQueue rows={dashboard?.awaiting_ack} onOpen={openMemo} />
            <div className="mt-4 flex gap-3.5 flex-wrap items-start">
              <DeptLoad rows={dashboard?.dept_load} />
              <ActivityFeed rows={dashboard?.activity} />
            </div>
            <MemoList memos={memos} onOpen={openMemo} onCleared={loadAll} />
          </>
        ) : tab === 'memos' ? (
          <>
            <FilterBar filters={openFilters} onChange={setOpenFilters}
              statusOptions={['analyzing', 'failed', 'open', 'pending-review']}
              deptOptions={deptOptions} people={people} isCompliance={isCompliance}
              shown={(listMemos ?? memos).length} total={memoCounts?.open_count} />
            <MemoList memos={listMemos ?? memos} onOpen={openMemo} onCleared={loadAll} title="Open memos"
              sortLabel={SORT_LABELS[openFilters.sort] ? SORT_LABELS[openFilters.sort].toLowerCase() : 'due date'}
              emptyText={filtersActive(openFilters)
                ? 'No open memo matches these filters.'
                : 'Nothing open. Anything finished lives in the Closed tab.'} />
          </>
        ) : tab === 'closed' ? (
          <>
            <FilterBar filters={closedFilters} onChange={setClosedFilters}
              statusOptions={['completed', 'retired']}
              deptOptions={deptOptions} people={people} isCompliance={isCompliance}
              shown={closed.length} total={memoCounts?.closed_count} />
            <MemoList memos={closed} onOpen={openMemo} onCleared={loadAll} title="Closed memos"
              sortLabel={SORT_LABELS[closedFilters.sort] ? SORT_LABELS[closedFilters.sort].toLowerCase() : 'last updated'}
              emptyText={filtersActive(closedFilters) ? 'No closed memo matches these filters.' : 'Nothing has closed yet.'} />
          </>
        ) : (
          <MyWork onOpen={openMemo} />
        )}
      </div>
    </div>
  );
}
